/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Outlet } from "react-router";

import { useToast } from "sj-ui-toolkit/src/hooks";

import {
  ActivityData,
  EvaluateRequestEntry,
  EvaluateRequestStatus,
  fetchDojoActivity,
  fetchEvaluateRequests,
} from "@/api/activities";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const OTHER_STATUSES: EvaluateRequestStatus[] = ["in_progress", "failed", "passed"];

export interface EvaluateFiltersState {
  selectedStatus: "all" | EvaluateRequestStatus;
  selectedAdmins: string[];
  selectedUsers: string[];
}

interface EvaluateRequestsContextValue {
  allActivities: ActivityData[];
  loading: boolean;
  loadingOther: boolean;
  loadedStatuses: Set<EvaluateRequestStatus>;
  error: string | null;
  loadEvaluateRequests: () => Promise<void>;
  updateActivity: (activityId: number, updates: Partial<ActivityData>) => void;
  filters: EvaluateFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<EvaluateFiltersState>>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const deduplicateActivities = (activities: ActivityData[]): ActivityData[] =>
  Array.from(new Map(activities.map(a => [a.id, a])).values());

const fetchActivityDetails = async (requests: EvaluateRequestEntry[]): Promise<ActivityData[]> => {
  const results = await Promise.all(
    requests.map(async req => {
      const activityData = await fetchDojoActivity(req.id, "judgment_request");
      if (!activityData) return null;
      const entry: ActivityData = {
        id: activityData.id,
        text: activityData.text,
        description:
          typeof activityData.description === "string" ? activityData.description : undefined,
        createdAt: req.updatedAt || req.createdAt,
        userName: req.name,
        requestStatus: req.status,
      };
      return entry;
    })
  );
  return results.filter((a): a is ActivityData => a !== null);
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const EvaluateRequestsContext = createContext<EvaluateRequestsContextValue | null>(null);

const DEFAULT_FILTERS: EvaluateFiltersState = {
  selectedStatus: "submitted",
  selectedAdmins: [],
  selectedUsers: [],
};

// ---------------------------------------------------------------------------
// Provider (used as a layout route wrapper in App.tsx)
// ---------------------------------------------------------------------------

export const EvaluateRequestsProvider = () => {
  const { showToast } = useToast();

  const [allActivities, setAllActivities] = useState<ActivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOther, setLoadingOther] = useState(false);
  const [loadedStatuses, setLoadedStatuses] = useState<Set<EvaluateRequestStatus>>(
    new Set(["submitted"])
  );
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<EvaluateFiltersState>(DEFAULT_FILTERS);

  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  const loadEvaluateRequests = useCallback(async () => {
    if (hasLoadedRef.current || isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const submittedRequests = await fetchEvaluateRequests({
        assigned_to: "anyone",
        order: "desc",
        status: "submitted",
      });
      const submittedActivities = await fetchActivityDetails(submittedRequests);
      setAllActivities(submittedActivities);
      setLoading(false);

      setLoadingOther(true);
      for (const status of OTHER_STATUSES) {
        const requests = await fetchEvaluateRequests({
          assigned_to: "anyone",
          order: "desc",
          status,
        });
        const activities = await fetchActivityDetails(requests);
        setAllActivities(prev => deduplicateActivities([...prev, ...activities]));
        setLoadedStatuses(prev => new Set([...prev, status]));
      }
      setLoadingOther(false);
      hasLoadedRef.current = true;
    } catch (err) {
      console.error("Failed to load evaluate requests:", err);
      setError("Failed to load evaluate requests");
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load evaluation requests",
      });
      setLoading(false);
      setLoadingOther(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, [showToast]);

  const updateActivity = useCallback((activityId: number, updates: Partial<ActivityData>) => {
    setAllActivities(prev =>
      prev.map(activity => (activity.id === activityId ? { ...activity, ...updates } : activity))
    );
  }, []);

  return (
    <EvaluateRequestsContext.Provider
      value={{
        allActivities,
        loading,
        loadingOther,
        loadedStatuses,
        error,
        loadEvaluateRequests,
        updateActivity,
        filters,
        setFilters,
      }}
    >
      <Outlet />
    </EvaluateRequestsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const FALLBACK: Pick<EvaluateRequestsContextValue, "updateActivity"> = {
  updateActivity: () => {},
};

export const useEvaluateRequests = (): EvaluateRequestsContextValue => {
  const ctx = useContext(EvaluateRequestsContext);
  if (!ctx) {
    return {
      ...FALLBACK,
      allActivities: [],
      loading: false,
      loadingOther: false,
      loadedStatuses: new Set<EvaluateRequestStatus>(),
      error: null,
      loadEvaluateRequests: async () => {},
      filters: {
        selectedStatus: "submitted",
        selectedAdmins: [],
        selectedUsers: [],
      },
      setFilters: () => {},
    };
  }
  return ctx;
};
